/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * RAG Documents Contract
 *
 * oRPC contract for uploading, listing, and managing RAG documents.
 *
 * @module contracts/rag-documents
 */

import { oc } from "@orpc/contract";
import * as z from "zod";

// Constants
/**
 * Per-file upload ceiling. The product rule is "each file at most 10 MB, the
 * org total at most the plan ceiling" — the org total is
 * `PLAN_LIMITS.storageBytes` (10/20/40 MiB by plan, benchmarked against
 * Chatbase), enforced server-side.
 */
export const MAX_RAG_FILE_BYTES = 10 * 1024 * 1024; // 10MB
/**
 * Staged (chat-composer) uploads get the SAME per-file ceiling: a larger one
 * would accept files that the smallest plan's org ceiling rejects on an empty
 * org.
 */
export const MAX_STAGED_RAG_FILE_BYTES = MAX_RAG_FILE_BYTES; // 10MB

export const STAGED_RAG_ALLOWED_MIME_TYPES = [
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"text/csv",
	"text/markdown",
	"text/plain",
	"application/zip",
] as const;

export const RAG_ALLOWED_MIME_TYPES = [
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"text/csv",
	"text/markdown",
	"text/plain",
	"image/png",
	"image/jpeg",
] as const;

export type RagMimeType = (typeof RAG_ALLOWED_MIME_TYPES)[number];

// Schemas
export const RagDocumentStatusSchema = z.enum([
	"PENDING",
	"PROCESSING",
	"COMPLETED",
	"FAILED",
]);

export type RagDocumentStatus = z.infer<typeof RagDocumentStatusSchema>;

/**
 * D4 — how ONE document's images are handled. Structural mirror of the server's
 * `RagImagePolicySchema` (`server/src/domain/schemas/rag/figure-meta.schema.ts`);
 * the same 4 values also appear as `RAG_IMAGE_POLICY_VALUES` on the config
 * boundary, which carries the ORG DEFAULT (`ragSettings.imagePolicy`).
 *
 * - "describe" (default) — vision-described figures spliced as text. INGEST-TIME
 * - "attach" — crop bytes handed to a vision model per turn. ANSWER-TIME
 * - "ocr" — the figure's verbatim text spliced. INGEST-TIME
 * - "ignore" — no description, no OCR, no stored crop. INGEST-TIME
 *
 * The three INGEST-TIME modes change the text baked into the chunks, so a change
 * only takes effect when the document is reprocessed.
 */
export const RagImagePolicySchema = z.enum([
	"describe",
	"attach",
	"ocr",
	"ignore",
]);
export type RagImagePolicy = z.infer<typeof RagImagePolicySchema>;

export const RagDocumentSchema = z.object({
	id: z.number().int().describe("Document ID"),
	filename: z.string().describe("Original filename"),
	mimeType: z.string().describe("MIME type"),
	sizeBytes: z.number().int().describe("File size in bytes"),
	status: RagDocumentStatusSchema.describe("Processing status"),
	errorMessage: z.string().nullable().describe("Error message if failed"),
	chunkCount: z.number().int().describe("Number of chunks created"),
	enabled: z.boolean().describe("Whether document is enabled for search"),
	createdAt: z.string().datetime().describe("Upload timestamp"),
	updatedAt: z.string().datetime().describe("Last update timestamp"),
	schemaPreload: z
		.boolean()
		.optional()
		.describe(
			"WS10.5 — whether this document schema is preloaded into the assistant system prompt"
		),
	hasColumnSchema: z
		.boolean()
		.optional()
		.describe(
			"WS10.5 — whether this document is tabular (has a parsed column schema). Gates the DB schema-preload toggle: it only succeeds for tabular docs."
		),
	sensitiveDataTypes: z
		.array(z.enum(["card", "iban", "national_id"]))
		.optional()
		.describe(
			"WS12 — sensitive-number families detected in the extracted text at ingest (absent when none)"
		),
	figureCount: z
		.number()
		.int()
		.optional()
		.describe(
			"WS11 — how many figures this document CONTAINS (not how many crops the ingest policy chose to store). Gates the image-policy picker, so an `ignore` ingest that stored nothing must still report them or the mode could never be undone."
		),
	imagePolicy: RagImagePolicySchema.optional().describe(
		"D4 — per-document image-handling OVERRIDE. Absent = the document inherits the org default (see `defaultImagePolicy` on the list response)."
	),
	includeImagesInAnswers: z
		.boolean()
		.optional()
		.describe(
			'WS11 — DERIVED mirror of `imagePolicy === "attach"`. Read-only for the UI; drive the 4-mode picker off `imagePolicy`.'
		),
	embeddingModel: z
		.string()
		.nullable()
		.optional()
		.describe(
			"Model the stored vectors were made with (searches use it); null until embedded"
		),
	retryable: z
		.boolean()
		.optional()
		.describe("FAILED with a transient error, so POST /retry can reprocess it"),
	timesRetrieved: z
		.number()
		.int()
		.optional()
		.describe("§5.6 — real-time count of searchDocs hits for this document"),
	lastRetrievedAt: z
		.string()
		.datetime()
		.nullable()
		.optional()
		.describe("§5.6 — last time a search surfaced this document"),
});

export type RagDocument = z.infer<typeof RagDocumentSchema>;

export const RagDocumentListResponseSchema = z.object({
	documents: z.array(RagDocumentSchema),
	total: z.number().int().describe("Total document count"),
	structuredTotal: z
		.number()
		.int()
		.optional()
		.describe("Org-wide count of documents carrying tabular (Excel/CSV) data"),
	defaultImagePolicy: RagImagePolicySchema.describe(
		"D4 — the org default (`ragSettings.imagePolicy`) every document with no `imagePolicy` override inherits. Shipped on the list so the UI can render the effective mode without a second config read."
	),
});

/**
 * Per-document meta PATCH (D4/D5).
 *
 * Send `imagePolicy` — it drives the 4-mode picker. `includeImagesInAnswers` is
 * retained ONLY so the pre-D4 boolean callers keep working. The translation is
 * asymmetric: `true` pins "attach" (the mode where the real image reaches the
 * model), while `false` CLEARS the override and returns the document to
 * inheriting the org default — it was the absence of an opt-in, never a request
 * to pin "describe", and a stale client must not be able to opt a document out
 * of inheritance for good. At least one of the two must be present.
 *
 * Served at `PATCH /api/orgs/{orgId}/rag/documents/{id}/meta` (an org-scoped
 * route outside the oRPC contract below, like the schema-preload toggle).
 */
export const UpdateDocumentMetaRequestSchema = z
	.object({
		imagePolicy: RagImagePolicySchema.optional(),
		includeImagesInAnswers: z.boolean().optional(),
	})
	.refine(
		(v) =>
			v.imagePolicy !== undefined || v.includeImagesInAnswers !== undefined,
		{ message: "Provide imagePolicy (or the legacy includeImagesInAnswers)" }
	);
export type UpdateDocumentMetaRequest = z.infer<
	typeof UpdateDocumentMetaRequestSchema
>;

export const UpdateDocumentMetaResponseSchema = z.object({
	id: z.number().int(),
	imagePolicy: RagImagePolicySchema.describe(
		"The EFFECTIVE policy after the write — the persisted per-document override, or the inherited org default when the write cleared the override (legacy `includeImagesInAnswers: false`)"
	),
	includeImagesInAnswers: z
		.boolean()
		.describe('Derived mirror of `imagePolicy === "attach"`'),
});
export type UpdateDocumentMetaResponse = z.infer<
	typeof UpdateDocumentMetaResponseSchema
>;

export const RagDocumentUploadResponseSchema = z.object({
	id: z.number().int().describe("Document ID"),
	filename: z.string().describe("Uploaded filename"),
	status: RagDocumentStatusSchema.describe("Initial status"),
	staged: z
		.boolean()
		.optional()
		.describe("Whether the upload is inert pending approval"),
});

// Embedding model schema
export const EmbeddingModelSchema = z.object({
	id: z.string().describe("Model ID"),
	name: z.string().describe("Display name"),
	description: z
		.string()
		.optional()
		.describe("Model description from OpenRouter"),
	contextLength: z.number().nullable().describe("Maximum context length"),
	pricing: z
		.object({
			prompt: z.number().describe("Price per prompt token"),
		})
		.optional()
		.describe("Pricing information"),
	architecture: z
		.object({
			modality: z
				.string()
				.optional()
				.describe('Composite modality string, e.g. "text+image->embeddings"'),
			inputModalities: z
				.array(z.string())
				.optional()
				.describe(
					"Input modalities supported by the model (text, image, audio, ...)"
				),
			outputModalities: z
				.array(z.string())
				.optional()
				.describe(
					'Output modalities produced by the model (typically ["embeddings"])'
				),
		})
		.optional()
		.describe("Architecture / modality info from OpenRouter"),
	supportedParameters: z
		.array(z.string())
		.optional()
		.describe("Supported OpenAI-compatible parameters surfaced by OpenRouter"),
});

export type EmbeddingModel = z.infer<typeof EmbeddingModelSchema>;

// Demo search result schema
export const DemoSearchResultSchema = z.object({
	documentId: z.number().int().describe("Document ID"),
	filename: z.string().describe("Document filename"),
	content: z.string().describe("Chunk content"),
	similarity: z.number().describe("Similarity score (0-1)"),
	enabled: z.boolean().describe("Whether document is enabled"),
});

export type DemoSearchResult = z.infer<typeof DemoSearchResultSchema>;

// WS10.5 — per-document schema preload into the system prompt (user-curated).
export const SchemaPreloadCurationSchema = z.object({
	excludedSheets: z.array(z.string()).default([]),
	excludedColumns: z.record(z.string(), z.array(z.string())).default({}),
});

export const SetSchemaPreloadRequestSchema = z.object({
	schemaPreload: z
		.boolean()
		.describe("Whether to preload this document schema into the system prompt"),
	schemaPreloadCuration: SchemaPreloadCurationSchema.optional().describe(
		"Sheets/columns to omit from the preloaded prompt section (tool output unaffected)"
	),
});

export const SchemaPreloadResponseSchema = z.object({
	id: z.number().int().describe("Document ID"),
	schemaPreload: z.boolean().describe("Resulting preload flag"),
	tokenEstimate: z
		.number()
		.int()
		.describe(
			"Estimated tokens the rendered schema section adds to the prompt"
		),
	warnThreshold: z
		.number()
		.int()
		.describe("UI warning threshold for the token estimate"),
});

export type SchemaPreloadResponse = z.infer<typeof SchemaPreloadResponseSchema>;

// ---------------------------------------------------------------------------
// Training-sources: sources summary (§5.2 / M6), manual refresh (§5.2 / M5),
// per-document usage stats (§5.6 / M8).
// ---------------------------------------------------------------------------

const SourceTypeTotalsSchema = z.object({
	count: z.number().int().describe("Number of sources of this type"),
	sizeBytes: z.number().int().describe("Total byte size across these sources"),
});

export const RagSourcesSummaryResponseSchema = z.object({
	files: SourceTypeTotalsSchema.describe("UPLOAD-sourced documents"),
	urls: SourceTypeTotalsSchema.describe("URL-sourced (crawled) documents"),
	qaPairs: SourceTypeTotalsSchema.describe("Curated Q&A pairs"),
	needsRefresh: z
		.boolean()
		.describe(
			"True when any doc FAILED, an embedding-model drift is detected, or a URL doc is stale"
		),
	staleUrlCount: z
		.number()
		.int()
		.describe("URL documents past the refresh interval"),
	lastRefreshedAt: z
		.string()
		.datetime()
		.nullable()
		.describe("MAX(refreshedAt) over the org URL docs (null when none)"),
});

export type RagSourcesSummaryResponse = z.infer<
	typeof RagSourcesSummaryResponseSchema
>;

export const RagRefreshResponseSchema = z.object({
	enqueued: z
		.number()
		.int()
		.describe("Crawl + re-embed jobs enqueued by the manual refresh"),
});

export type RagRefreshResponse = z.infer<typeof RagRefreshResponseSchema>;

export const RagDocumentStatsQuerySchema = z.object({
	days: z.coerce.number().int().min(1).max(365).optional().default(90),
});

export const RagUsageDailyPointSchema = z.object({
	day: z.string().describe("ISO date (YYYY-MM-DD)"),
	count: z.number().int().describe("Retrieval hits that day"),
});

export const RagDocumentStatsResponseSchema = z.object({
	timesRetrieved: z.number().int(),
	lastRetrievedAt: z.string().datetime().nullable(),
	createdAt: z.string().datetime(),
	series: z.array(RagUsageDailyPointSchema),
});

export type RagDocumentStatsResponse = z.infer<
	typeof RagDocumentStatsResponseSchema
>;

// Contract
export const ragDocumentsContract = {
	/**
	 * Upload a document for RAG indexing
	 */
	upload: oc
		.route({
			method: "POST",
			path: "/api/rag/documents/upload",
			summary: "Upload document for RAG indexing",
			description:
				"Upload a PDF, DOCX, Excel, CSV, MD, TXT, PNG, or JPG file for semantic search indexing. Processing happens asynchronously.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				// MIME validation done in handler for better error messages
				// (browsers may report different MIME types for same file extension)
				file: z.file(),
			})
		)
		.output(RagDocumentUploadResponseSchema),

	/**
	 * List all uploaded documents
	 */
	list: oc
		.route({
			method: "GET",
			path: "/api/rag/documents",
			summary: "List RAG documents",
			description: "Get all uploaded documents with their processing status.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				limit: z.coerce.number().int().min(1).max(100).optional().default(50),
				cursor: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(RagDocumentListResponseSchema),

	/**
	 * Get document status (for polling)
	 */
	getStatus: oc
		.route({
			method: "GET",
			path: "/api/rag/documents/{id}/status",
			summary: "Get document status",
			description: "Get the current processing status of a document.",
			tags: ["RAG Documents"],
		})
		.input(z.object({ id: z.coerce.number().int().min(1) }))
		.output(RagDocumentSchema),

	/**
	 * Delete a document
	 */
	delete: oc
		.route({
			method: "DELETE",
			path: "/api/rag/documents/{id}",
			summary: "Delete RAG document",
			description:
				"Delete a document and all its chunks from the index. This action cannot be undone.",
			tags: ["RAG Documents"],
		})
		.input(z.object({ id: z.coerce.number().int().min(1) }))
		.output(z.object({ success: z.literal(true) })),

	/**
	 * Toggle document enabled status
	 */
	toggleEnabled: oc
		.route({
			method: "PATCH",
			path: "/api/rag/documents/{id}/enabled",
			summary: "Toggle document enabled status",
			description:
				"Enable or disable a document for search without deleting it.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				enabled: z.boolean(),
			})
		)
		.output(RagDocumentSchema),

	/**
	 * Toggle/curate per-document schema preload (WS10.5)
	 */
	setSchemaPreload: oc
		.route({
			method: "PATCH",
			path: "/api/rag/documents/{id}/schema-preload",
			summary: "Toggle document schema preload",
			description:
				"Preload a tabular document's per-sheet schema into the assistant system prompt so structured questions skip the getDocumentSchemas round-trip. Capped per org.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				schemaPreload: z.boolean(),
				schemaPreloadCuration: SchemaPreloadCurationSchema.optional(),
			})
		)
		.output(SchemaPreloadResponseSchema),

	/**
	 * List available embedding models from OpenRouter
	 */
	listEmbeddingModels: oc
		.route({
			method: "GET",
			path: "/api/rag/embedding-models",
			summary: "List embedding models",
			description: "Get available embedding models from OpenRouter.",
			tags: ["RAG Documents"],
		})
		.output(
			z.object({
				models: z.array(EmbeddingModelSchema),
			})
		),

	/**
	 * Re-embed all documents (after changing embedding model)
	 */
	reembedAll: oc
		.route({
			method: "POST",
			path: "/api/rag/documents/reembed-all",
			summary: "Re-embed all documents",
			description:
				"Delete all chunks and re-process all documents with current embedding model.",
			tags: ["RAG Documents"],
		})
		.output(
			z.object({
				documentsQueued: z
					.number()
					.int()
					.describe("Number of documents queued"),
				message: z.string().describe("Status message"),
			})
		),

	/**
	 * Demo search for testing queries
	 */
	demoSearch: oc
		.route({
			method: "POST",
			path: "/api/rag/documents/demo-search",
			summary: "Demo search",
			description:
				"Test search queries against indexed documents with current settings.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				query: z.string().trim().min(1).max(500).describe("Search query"),
				limit: z
					.number()
					.int()
					.min(1)
					.max(20)
					.optional()
					.default(5)
					.describe("Max results"),
				threshold: z
					.number()
					.min(0)
					.max(1)
					.optional()
					.describe("Override threshold"),
			})
		)
		.output(
			z.object({
				results: z.array(DemoSearchResultSchema),
				queryTimeMs: z.number().describe("Query time in milliseconds"),
				totalDocuments: z.number().int().describe("Total indexed documents"),
				tokensUsed: z
					.number()
					.int()
					.describe("Tokens used for query embedding"),
				embeddingModel: z.string().describe("Embedding model used"),
			})
		),

	/**
	 * Scrape a URL and process pages through the RAG pipeline
	 */
	scrapeUrl: oc
		.route({
			method: "POST",
			path: "/api/rag/documents/scrape-url",
			summary: "Scrape URL for RAG indexing",
			description:
				"Crawl a URL, extract content as markdown, and process each page through the RAG document pipeline.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				url: z.string().url().describe("URL to scrape"),
				crawlDepth: z
					.number()
					.int()
					.min(1)
					.max(100)
					.optional()
					.default(50)
					.describe("Maximum number of pages to crawl"),
			})
		)
		.output(
			z.object({
				pagesQueued: z
					.number()
					.int()
					.describe("Number of pages queued for processing"),
				pagesSkipped: z
					.number()
					.int()
					.describe("Number of pages skipped (unchanged content)"),
				message: z.string().describe("Status message"),
			})
		),

	/**
	 * Inspect chunks for a single document.
	 *
	 * Returns the persisted chunk rows (content, token count, type, page/sheet
	 * metadata, embedding dimensions) so the RAG playground can visualize what
	 * the indexing pipeline produced from a given upload. Does NOT return raw
	 * embedding vectors -- only the dimension count.
	 */
	getChunks: oc
		.route({
			method: "GET",
			path: "/api/rag/documents/{id}/chunks",
			summary: "List chunks for a document",
			description:
				"Get the chunk rows produced for a document, with content, token count, and embedding dimension.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				limit: z.coerce.number().int().min(1).max(500).optional().default(100),
				cursor: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(
			z.object({
				documentId: z.number().int(),
				filename: z.string(),
				total: z.number().int(),
				chunks: z.array(
					z.object({
						id: z.number().int(),
						chunkIndex: z.number().int(),
						content: z.string(),
						tokenCount: z.number().int().nullable(),
						contextPrefix: z.string().nullable(),
						pageNumber: z.number().int().nullable(),
						sheetName: z.string().nullable(),
						rowRange: z.string().nullable(),
						chunkType: z.enum([
							"TEXT",
							"TABLE",
							"MIXED",
							"SECTION",
							"CODE_BLOCK",
						]),
						embeddingDimensions: z
							.number()
							.int()
							.nullable()
							.describe("Number of dimensions in the stored embedding vector"),
						metadata: z.unknown().nullable(),
						createdAt: z.string().datetime(),
					})
				),
			})
		),

	/**
	 * Debug endpoint to test embedding similarity between query and text
	 */
	debugEmbedding: oc
		.route({
			method: "POST",
			path: "/api/rag/debug-embedding",
			summary: "Debug embedding similarity",
			description:
				"Test embedding similarity between a query and a text sample",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				query: z.string().trim().min(1).describe("Search query"),
				text: z.string().trim().min(1).describe("Text to compare against"),
			})
		)
		.output(
			z.object({
				querySample: z.string().describe("First 100 chars of query embedding"),
				textSample: z.string().describe("First 100 chars of text embedding"),
				similarity: z.number().describe("Cosine similarity between embeddings"),
				model: z.string().describe("Embedding model used"),
			})
		),

	/**
	 * WS5 / M1 — replace a document's content under the same id (multipart). Old
	 * chunks are wiped and the new bytes reprocessed; the id (and its usage
	 * counters) survive. This is the honest "re-upload to update".
	 */
	replace: oc
		.route({
			method: "PUT",
			path: "/api/rag/documents/{id}/replace",
			summary: "Replace RAG document content",
			description:
				"Reprocess a document under the same id with new file bytes. Old chunks are deleted and the new content re-embedded. No duplicate is created.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				file: z.file(),
			})
		)
		.output(RagDocumentUploadResponseSchema),

	/**
	 * §5.2 / M6 — per-source-type totals + `needsRefresh` / `staleUrlCount` /
	 * `lastRefreshedAt`.
	 */
	sourcesSummary: oc
		.route({
			method: "GET",
			path: "/api/rag/sources/summary",
			summary: "RAG sources summary",
			description:
				"Per-source-type {count, sizeBytes} (files, URLs, Q&A pairs) plus a needsRefresh flag, stale URL count, and the last refreshed timestamp.",
			tags: ["RAG Documents"],
		})
		.output(RagSourcesSummaryResponseSchema),

	/**
	 * §5.2 / M5 — manual "Refresh knowledge": re-crawl every URL root (≤ 15) and
	 * re-embed on model drift. Returns the number of jobs enqueued.
	 */
	refreshNow: oc
		.route({
			method: "POST",
			path: "/api/rag/refresh",
			summary: "Refresh knowledge now",
			description:
				"Re-crawl all URL sources and re-embed drifted documents/Q&A immediately. Returns the count of crawl + re-embed jobs enqueued.",
			tags: ["RAG Documents"],
		})
		.output(RagRefreshResponseSchema),

	/**
	 * §5.6 / M8 — per-document usage insight: times retrieved, last retrieved,
	 * created-at, and a daily-frequency series (`?days=90`).
	 */
	documentStats: oc
		.route({
			method: "GET",
			path: "/api/rag/documents/{id}/stats",
			summary: "RAG document usage stats",
			description:
				"Times retrieved, last-retrieved timestamp, created-at, and a daily-frequency series for one document.",
			tags: ["RAG Documents"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				days: z.coerce.number().int().min(1).max(365).optional().default(90),
			})
		)
		.output(RagDocumentStatsResponseSchema),
};

export type RagDocumentsContract = typeof ragDocumentsContract;
